import React from 'react'
import { observer } from 'mobx-react-lite'

import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { H3, P, Text } from '@mxenabled/mxui'

import {
  Loader,
  useGlobalCopyStore,
  useGlobalUiStore,
  useScreenSize,
  useSettingsStore,
} from '../../../common'

import type { Device } from '../../../core'

import { DialogType } from '../../constants/Settings'
import DeviceTable from './DeviceTable'
import DeviceList from './DeviceList'
import DeviceDialog from './DeviceDialog'

export const DeviceTab = observer(() => {
  const { isMobile } = useScreenSize()
  const { settings: settingsCopy } = useGlobalCopyStore()
  const { isCopyLoaded, isInitialized } = useGlobalUiStore()
  const { setAlert, devices, loadDevices, generateDeviceToken } = useSettingsStore()

  const [dialog, setDialog] = React.useState({ open: false, type: '' })
  const [selectedDevice, setSelectedDevice] = React.useState<Device | undefined>()
  const [deviceToken, setDeviceToken] = React.useState('')

  React.useEffect(() => {
    if (isInitialized) {
      loadDevices()
    }
  }, [isInitialized])

  const handleGenerateDeviceToken = async () => {
    const token = await generateDeviceToken()
    if (token) {
      setDeviceToken(token)
      setDialog({ open: true, type: DialogType.Token })
    } else {
      setAlert(settingsCopy.mobile_devices_access_error, 'error')
    }
  }

  const handleClickDeviceEdit = (device: Device) => {
    setSelectedDevice(device)
    setDialog({ open: true, type: DialogType.Edit })
  }

  const handleClickDeviceDelete = (device: Device) => {
    setSelectedDevice(device)
    setDialog({ open: true, type: DialogType.Delete })
  }

  const handleCloseDialog = () => {
    setDialog({ open: false, type: '' })
    setSelectedDevice(undefined)
  }

  if (!isCopyLoaded || !isInitialized) {
    return <Loader />
  }

  return (
    <Stack
      sx={{
        backgroundColor: 'background.paper',
        p: 24,
        gap: 24,
      }}
    >
      <Stack gap={4}>
        <H3>{settingsCopy.mobile_devices_title}</H3>
        <P variant="body1">{settingsCopy.mobile_devices_info}</P>
      </Stack>

      {/* Device Table/List */}
      {isMobile ? (
        <DeviceList
          devices={devices}
          onClickDeviceDelete={handleClickDeviceDelete}
          onClickDeviceEdit={handleClickDeviceEdit}
        />
      ) : (
        <DeviceTable
          devices={devices}
          onClickDeviceDelete={handleClickDeviceDelete}
          onClickDeviceEdit={handleClickDeviceEdit}
        />
      )}

      {/* Token CTA */}
      <Stack sx={{ alignItems: 'center', px: 24, py: 12, gap: 4 }}>
        <Text bold={true} variant="body1">
          {settingsCopy.mobile_devices_access_title}
        </Text>
        <P align="center" variant="body2">
          {settingsCopy.mobile_devices_access_description}
        </P>
        <Button onClick={handleGenerateDeviceToken} variant="text">
          {settingsCopy.mobile_devices_access_cta}
        </Button>
      </Stack>

      {/* Dialogs (Token, Edit, or Delete) */}
      <DeviceDialog
        device={selectedDevice}
        deviceToken={deviceToken}
        dialogType={dialog.type}
        isOpen={dialog.open}
        onClose={handleCloseDialog}
      />
    </Stack>
  )
})

export default DeviceTab
